import { useMemo, useState } from 'react';

/** Default rows per page for every Field Sales table (the HTML has no pager). */
export const FS_PAGE_SIZE = 50;

/**
 * Client-side pagination helper. Returns the current page slice plus the pager
 * state. `resetKey` resets to page 1 whenever it changes (e.g. the dimension,
 * type, quad, or filters changed), so a page index never dangles past the end.
 *
 * @param {Array} rows
 * @param {{ pageSize?: number, resetKey?: unknown }} [options]
 */
export function usePagedRows(rows, { pageSize = FS_PAGE_SIZE, resetKey } = {}) {
  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever the data identity (resetKey) changes.
  const [lastKey, setLastKey] = useState(resetKey);
  if (resetKey !== lastKey) {
    setLastKey(resetKey);
    if (page !== 1) setPage(1);
  }

  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = useMemo(
    () => list.slice((safePage - 1) * pageSize, safePage * pageSize),
    [list, safePage, pageSize]
  );
  // Row offset so a "#" column can show a global index across pages.
  const offset = (safePage - 1) * pageSize;

  return { pageRows, page: safePage, setPage, pageCount, total, pageSize, offset };
}
